import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, TextField, Typography } from '@mui/material';
import { Link as LinkIcon, LinkOff as LinkOffIcon, Save as SaveIcon } from '@mui/icons-material';
import olxService, { type OlxConnection } from '../../services/olxService';

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;

const OlxIntegrationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connection, setConnection] = useState<OlxConnection | null>(null);
  const [zipcode, setZipcode] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const callbackHandled = useRef(false);

  const applyConnection = (data: OlxConnection) => {
    setConnection(data);
    setZipcode(data.zipcode || '');
    setPhone(data.phone || '');
  };

  const load = useCallback(async () => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const olxError = searchParams.get('error');

    setLoading(true);
    try {
      if ((code && state) || olxError) {
        // O código da OLX só vale uma vez.
        if (callbackHandled.current) return;
        callbackHandled.current = true;
        setSearchParams({}, { replace: true });
      }
      if (olxError) {
        setError('A conexão com a OLX foi cancelada ou não autorizada.');
      } else if (code && state) {
        applyConnection(await olxService.finishConnection(code, state));
        setSuccess('Conta OLX conectada.');
        return;
      }
      applyConnection(await olxService.getConnection());
    } catch (e: any) {
      setError(errorMessage(e, 'Não foi possível carregar a integração com a OLX.'));
    } finally {
      setLoading(false);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      window.location.href = await olxService.getConnectUrl();
    } catch (e: any) {
      setError(errorMessage(e, 'Não foi possível iniciar a conexão com a OLX.'));
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar a conta OLX? Os anúncios já publicados continuam no ar.')) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      applyConnection(await olxService.disconnect());
      setSuccess('Conta OLX desconectada.');
    } catch (e: any) {
      setError(errorMessage(e, 'Não foi possível desconectar.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      applyConnection(await olxService.saveSettings(zipcode, phone));
      setSuccess('Dados dos anúncios salvos.');
    } catch (e: any) {
      setError(errorMessage(e, 'Não foi possível salvar.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 760 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Integração OLX
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Conecte a conta OLX da loja para publicar veículos direto pela tela do veículo. É necessário um plano Empresa ativo.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {connection && !connection.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          As credenciais da OLX ainda não foram configuradas no servidor. Elas são fornecidas pela OLX ao aprovar o
          cadastro do integrador.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6">Conta da loja</Typography>
              <Chip
                size="small"
                variant="outlined"
                color={connection?.connected ? 'success' : 'default'}
                label={connection?.connected ? 'Conectada' : 'Não conectada'}
              />
            </Box>
            {connection?.connected && (
              <Typography variant="body2" color="text.secondary">
                {[connection.account_name, connection.account_email].filter(Boolean).join(' · ') || 'Conta autorizada'}
              </Typography>
            )}
          </Box>
          {connection?.connected ? (
            <Button variant="outlined" color="inherit" startIcon={<LinkOffIcon />} onClick={() => void handleDisconnect()} disabled={busy}>
              Desconectar
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => void handleConnect()}
              disabled={busy || !connection?.configured}
            >
              Conectar OLX
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Dados dos anúncios
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Usados em todos os anúncios enviados para a OLX.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="CEP da loja" value={zipcode} onChange={(e) => setZipcode(e.target.value)} inputProps={{ maxLength: 9 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Telefone com DDD" value={phone} onChange={(e) => setPhone(e.target.value)} inputProps={{ maxLength: 16 }} />
          </Grid>
        </Grid>
        <Button sx={{ mt: 2 }} variant="contained" startIcon={<SaveIcon />} onClick={() => void handleSave()} disabled={busy || !zipcode || !phone}>
          Salvar
        </Button>
      </Paper>
    </Box>
  );
};

export default OlxIntegrationPage;
